import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Crown, Lock, X, Check, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProBadge = ({ className = '' }) => {
  return (
    <span className={`badge-pro inline-flex items-center gap-1 ${className}`}>
      <Crown className="w-3 h-3" /> PRO
    </span>
  );
};

export const ProLock = ({ feature, onUpgrade }) => {
  return (
    <div className="pro-lock-overlay" onClick={onUpgrade}>
      <div className="flex flex-col items-center text-slate-700">
        <Lock className="w-8 h-8 mb-2" />
        <p className="font-medium text-sm">{feature}</p>
        <p className="text-xs text-slate-500">Requiere PRO</p>
      </div>
    </div>
  );
};

export const UpgradeModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ origin_url: window.location.origin })
      });

      if (!response.ok) {
        throw new Error('Error al crear sesión de pago');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      setLoading(false);
    }
  };

  const features = [
    'Clientes ilimitados',
    'Módulo de Inventario completo',
    'Módulo de Finanzas con reportes',
    'Exportación a CSV',
    'Soporte prioritario',
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="w-6 h-6 text-amber-500" />
            Actualiza a TallerMec PRO
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <div className="subscription-card mb-6">
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-4xl font-bold">$299</span>
              <span className="text-white/70">MXN/mes</span>
            </div>
            <p className="text-white/80 text-sm">Acceso completo a todas las funciones</p>
          </div>

          <ul className="space-y-3 mb-6">
            {features.map((feature, i) => (
              <li key={i} className="flex items-center gap-3 text-sm">
                <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-emerald-600" />
                </div>
                {feature}
              </li>
            ))}
          </ul>

          <div className="space-y-3">
            <Button
              onClick={handleUpgrade}
              disabled={loading}
              className="w-full bg-gradient-to-r from-amber-400 to-amber-600 hover:from-amber-500 hover:to-amber-700 text-white"
              data-testid="upgrade-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Crown className="w-4 h-4 mr-2" />
              )}
              Actualizar Ahora
            </Button>
            <Button variant="outline" onClick={onClose} className="w-full">
              Continuar con plan gratuito
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const useProFeature = () => {
  const { isPro } = useAuth();
  const [showUpgrade, setShowUpgrade] = useState(false);

  const checkProAccess = (callback) => {
    if (isPro) {
      callback();
    } else {
      setShowUpgrade(true);
    }
  };

  return {
    isPro,
    showUpgrade,
    setShowUpgrade,
    checkProAccess,
    UpgradeModalComponent: () => (
      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} />
    )
  };
};
